import React, { useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import ReversalRecordsDrawer from './ReversalRecordsDrawer';
interface ReversalUpload {
  id: string;
  fileName: string;
  uploadedBy: string;
  uploadedAt: string;
  totalRecords: number;
  processedRecords: number;
  failedRecords: number;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'PARTIAL';
}
interface ReversalRecord {
  id: string;
  transactionId: string;
  mtoName: string;
  beneficiaryName: string;
  amount: string;
  currency: string;
  reversalReason: string;
  status: 'PENDING' | 'PROCESSED' | 'FAILED';
  processedAt?: string;
}
// Mock data
const MOCK_REVERSAL_UPLOADS: ReversalUpload[] = Array(15).fill(null).map((_, index) => {
  const statuses: ReversalUpload['status'][] = ['PROCESSING', 'COMPLETED', 'FAILED', 'PARTIAL'];
  const status = statuses[index % 4];
  const totalRecords = 50 + index * 10;
  let processedRecords = 0;
  let failedRecords = 0;
  if (status === 'COMPLETED') {
    processedRecords = totalRecords;
    failedRecords = 0;
  } else if (status === 'FAILED') {
    processedRecords = 0;
    failedRecords = totalRecords;
  } else if (status === 'PARTIAL') {
    processedRecords = Math.floor(totalRecords * 0.7);
    failedRecords = totalRecords - processedRecords;
  } else {
    processedRecords = Math.floor(totalRecords * 0.3);
    failedRecords = 0;
  }
  return {
    id: `REV-${10000 + index}`,
    fileName: `reversal_batch_${index + 1}_${new Date().toISOString().split('T')[0]}.xlsx`,
    uploadedBy: ['Admin User', 'John Doe', 'Jane Smith'][index % 3],
    uploadedAt: new Date(Date.now() - index * 3600000).toISOString(),
    totalRecords,
    processedRecords,
    failedRecords,
    status
  };
});
// Generate mock reversal records for a given upload
const generateMockReversalRecords = (upload: ReversalUpload): ReversalRecord[] => {
  const records: ReversalRecord[] = [];
  const mtos = ['RIA Money Transfer', 'InstantCash', 'MoneyGram', 'Western Union'];
  const reasons = ['Duplicate transaction', 'Customer request', 'Incorrect amount', 'Wrong beneficiary', 'Compliance issue'];
  for (let i = 0; i < upload.totalRecords; i++) {
    let recordStatus: 'PENDING' | 'PROCESSED' | 'FAILED' = 'PENDING';
    let processedAt: string | undefined = undefined;
    if (upload.status === 'COMPLETED') {
      recordStatus = 'PROCESSED';
      processedAt = new Date(Date.now() - i * 60000).toISOString();
    } else if (upload.status === 'FAILED') {
      recordStatus = 'FAILED';
      processedAt = new Date(Date.now() - i * 60000).toISOString();
    } else if (upload.status === 'PARTIAL') {
      if (i < upload.processedRecords) {
        recordStatus = 'PROCESSED';
        processedAt = new Date(Date.now() - i * 60000).toISOString();
      } else if (i < upload.processedRecords + upload.failedRecords) {
        recordStatus = 'FAILED';
        processedAt = new Date(Date.now() - i * 60000).toISOString();
      }
    }
    records.push({
      id: `REC-${i + 1}`,
      transactionId: `TXN-${20000 + i}`,
      mtoName: mtos[i % mtos.length],
      beneficiaryName: `Beneficiary ${i + 1}`,
      amount: (1000 + i * 50).toFixed(2),
      currency: 'USD',
      reversalReason: reasons[i % reasons.length],
      status: recordStatus,
      processedAt
    });
  }
  return records;
};
const ReversalListsModal: React.FC = () => {
  const [uploads, setUploads] = useState<ReversalUpload[]>(MOCK_REVERSAL_UPLOADS);
  const [selectedUpload, setSelectedUpload] = useState<ReversalUpload | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reversalRecords, setReversalRecords] = useState<ReversalRecord[]>([]);
  const handleViewDetails = (upload: ReversalUpload) => {
    setSelectedUpload(upload);
    setReversalRecords(generateMockReversalRecords(upload));
    setDrawerOpen(true);
  };
  const rowActions: RowAction<ReversalUpload>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => handleViewDetails(row)
  }];
  const columns: Column<ReversalUpload>[] = [{
    id: 'id',
    label: 'Upload ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-2081" data-mp="3sqyc9igut9lq">
          {row.id}
        </Typography>
  }, {
    id: 'fileName',
    label: 'File Name',
    renderCell: row => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      minWidth: 250
    }} data-id="element-2082" data-mp="kcpmsj9moyxs">
          <Icon icon="lucide:file-spreadsheet" width={16} height={16} color="#10B981" data-id="element-2083" data-mp="2m42rp8blj6tu" />
          <Typography variant="body2" noWrap sx={{
        maxWidth: 300
      }} data-id="element-2084" data-mp="2h7ccen7m8f9h">
            {row.fileName}
          </Typography>
        </Box>
  }, {
    id: 'uploadedBy',
    label: 'Uploaded By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-2085" data-mp="mvl07stt5e56">
          {row.uploadedBy}
        </Typography>
  }, {
    id: 'uploadedAt',
    label: 'Upload Date',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-2086" data-mp="dzby26eg41fc">
          {new Date(row.uploadedAt).toLocaleString()}
        </Typography>
  }, {
    id: 'totalRecords',
    label: 'Total Records',
    renderCell: row => <Chip label={row.totalRecords} size="small" sx={{
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
      color: '#6366F1',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-2087" data-mp="1yckrcl4f5mxm" />
  }, {
    id: 'processedRecords',
    label: 'Processed',
    renderCell: row => <Chip label={row.processedRecords} size="small" sx={{
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#10B981',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-2088" data-mp="1rwkjdbczglh4" />
  }, {
    id: 'failedRecords',
    label: 'Failed',
    renderCell: row => <Chip label={row.failedRecords} size="small" sx={{
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-2089" data-mp="2fmoltfefqf6j" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => {
      const colors = {
        PROCESSING: {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        COMPLETED: {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        },
        FAILED: {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        },
        PARTIAL: {
          bg: 'rgba(245, 158, 11, 0.1)',
          color: '#F59E0B'
        }
      };
      const style = colors[row.status];
      return <Chip label={row.status} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-2090" data-mp="140644yvlgmuc" />;
    }
  }];
  return <>
      <DataTable title="Reversal Upload History" data={uploads} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No reversal uploads found" initialVisibleColumns={['id', 'fileName', 'uploadedBy', 'uploadedAt', 'totalRecords', 'processedRecords', 'failedRecords', 'status']} onRowClick={handleViewDetails} data-id="element-2091" data-mp="1hrkmr4llam0v" />

      {selectedUpload && <ReversalRecordsDrawer open={drawerOpen} onClose={() => {
      setDrawerOpen(false);
      setSelectedUpload(null);
    }} uploadId={selectedUpload.id} fileName={selectedUpload.fileName} records={reversalRecords} data-id="element-2092" data-mp="1bnjhtcrfw7zx" />}
    </>;
};
export default ReversalListsModal;